import { Avatar, Box, CircularProgress, Fade, Typography } from '@mui/material';
import { ReactNode, useCallback } from 'react';
import { Accept, DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { FileUpload } from 'src/icons/file-upload';
import { FileSizeUnit } from 'src/types/enum/file-size-unit';
import { calculateFileSizeFromBytes } from 'src/utils/calculate-file-size-from-bytes';

interface Props {
  loading: boolean;
  onChange: (files: File[]) => void;
  multiple?: boolean;
  maxFiles?: number;
  accept?: Accept;
  children?: ReactNode;
  disabled?: boolean;
  backgroundColor?: string;
  maxSizeInBytes?: number;
  sizeUnit?: FileSizeUnit;
  onDropRejected?: (fileRejections: FileRejection[], event: DropEvent) => void;
}

export function FileDropzone({
  loading,
  onChange,
  multiple,
  maxFiles,
  accept,
  children,
  disabled = false,
  backgroundColor = 'transparent',
  maxSizeInBytes,
  sizeUnit,
  onDropRejected,
}: Props) {
  const { t } = useTranslation();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onChange(acceptedFiles);
    },
    [onChange],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple,
    maxFiles,
    accept,
    disabled,
    maxSize: maxSizeInBytes,
    onDropRejected,
  });

  const supportedFileFormats = accept
    ? Object.entries(accept)
        .map(option => option[1][0])
        .join(', ')
    : '';

  return (
    <Box
      sx={{
        border: '1px',
        borderStyle: 'dashed',
        borderColor: 'neutral.300',
        borderRadius: '8px',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        backgroundColor,
        padding: 2,
      }}
      {...getRootProps()}>
      {loading ? (
        <Fade in={loading} unmountOnExit>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            <CircularProgress size={24} />
          </Box>
        </Fade>
      ) : (
        <>
          <input {...getInputProps()} />

          {children ? (
            children
          ) : (
            <>
              <Avatar
                sx={{
                  backgroundColor: '#FBFAF9',
                  color: 'text.secondary',
                  height: 36,
                  width: 36,
                }}>
                <FileUpload />
              </Avatar>

              <Typography variant="body2">
                {t('common:fileDropzone.browse')}
              </Typography>

              <Typography
                align="center"
                sx={{ color: 'text.secondary' }}
                variant="caption">
                {t('common:fileDropzone.orDrop')}
              </Typography>

              {supportedFileFormats ? (
                <Typography
                  align="center"
                  color="textSecondary"
                  variant="caption">
                  {t('common:fileDropzone.format', {
                    formats: supportedFileFormats,
                  })}
                </Typography>
              ) : null}

              {maxSizeInBytes && sizeUnit ? (
                <Typography
                  align="center"
                  color="textSecondary"
                  variant="caption">
                  {t('common:fileDropzone.maxFileSize', {
                    size: calculateFileSizeFromBytes(maxSizeInBytes, sizeUnit),
                    unit: sizeUnit,
                  })}
                </Typography>
              ) : null}
            </>
          )}
        </>
      )}
    </Box>
  );
}
